import React, { useMemo, useState } from "react";
import { TEMPLATE_COLUMNS } from "./reportColumnCatalog";

const DRAG_TYPE = "application/x-report-template-column";

export default function ReportTemplateColumnsEditor({
  columnOrder = [],
  visibleColumns = {},
  columnLabels = {},
  onChange,
}) {
  const fullOrder = useMemo(() => {
    const validKeys = new Set(TEMPLATE_COLUMNS.map((column) => column.viewKey));
    const seen = new Set();
    const saved = columnOrder.filter((key) => {
      if (!validKeys.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return [
      ...saved,
      ...TEMPLATE_COLUMNS.map((column) => column.viewKey).filter((key) => !seen.has(key)),
    ];
  }, [columnOrder]);

  const [activeGroup, setActiveGroup] = useState("Core");
  const [dragOverKey, setDragOverKey] = useState(null);
  const [dragOverPanel, setDragOverPanel] = useState(null);

  const byKey = useMemo(() => {
    const map = {};
    for (const column of TEMPLATE_COLUMNS) map[column.viewKey] = column;
    return map;
  }, []);

  const groups = useMemo(() => {
    const names = [];
    for (const column of TEMPLATE_COLUMNS) {
      if (!names.includes(column.group)) names.push(column.group);
    }
    return names;
  }, []);

  const isEnabled = (key) => visibleColumns[key] !== false;
  const selectedKeys = fullOrder.filter(isEnabled);
  const availableKeys = TEMPLATE_COLUMNS
    .filter((column) => column.group === activeGroup && !isEnabled(column.viewKey))
    .map((column) => column.viewKey);

  const emit = (next) => {
    onChange?.({
      columnOrder: next.columnOrder ?? fullOrder,
      visibleColumns: next.visibleColumns ?? visibleColumns,
      columnLabels: next.columnLabels ?? columnLabels,
    });
  };

  const addColumn = (viewKey, beforeKey = null) => {
    if (!viewKey || isEnabled(viewKey) || !byKey[viewKey]) return;
    const order = [...fullOrder];
    const sourceIndex = order.indexOf(viewKey);
    if (sourceIndex !== -1) order.splice(sourceIndex, 1);

    // Clicking Add appends the new field after every currently selected field.
    // When dragging into a specific position in the selected panel, honor that
    // drop target instead.
    if (beforeKey && isEnabled(beforeKey)) {
      const targetIndex = order.indexOf(beforeKey);
      if (targetIndex !== -1) order.splice(targetIndex, 0, viewKey);
      else order.push(viewKey);
    } else {
      order.push(viewKey);
    }
    emit({
      columnOrder: order,
      visibleColumns: { ...visibleColumns, [viewKey]: true },
    });
  };

  const removeColumn = (viewKey) => {
    if (!viewKey || !isEnabled(viewKey)) return;
    emit({ visibleColumns: { ...visibleColumns, [viewKey]: false } });
  };

  const setLabel = (viewKey, value) => {
    emit({ columnLabels: { ...columnLabels, [viewKey]: value } });
  };

  const reorderSelected = (sourceKey, targetKey) => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;
    if (!isEnabled(sourceKey) || !isEnabled(targetKey)) return;
    const selected = fullOrder.filter(isEnabled);
    const sourceIndex = selected.indexOf(sourceKey);
    const targetIndex = selected.indexOf(targetKey);
    if (sourceIndex < 0 || targetIndex < 0) return;
    selected.splice(targetIndex, 0, selected.splice(sourceIndex, 1)[0]);
    let selectedIndex = 0;
    const nextOrder = fullOrder.map((key) =>
      isEnabled(key) ? selected[selectedIndex++] : key,
    );
    emit({ columnOrder: nextOrder });
  };

  const move = (viewKey, direction) => {
    const selected = fullOrder.filter(isEnabled);
    const index = selected.indexOf(viewKey);
    const target = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= selected.length) return;
    reorderSelected(viewKey, selected[target]);
  };

  const getDraggedKey = (event) =>
    event.dataTransfer.getData(DRAG_TYPE) || event.dataTransfer.getData("text/plain");

  const handleSelectedDrop = (event, targetKey = null) => {
    event.preventDefault();
    const sourceKey = getDraggedKey(event);
    if (isEnabled(sourceKey)) {
      if (targetKey) reorderSelected(sourceKey, targetKey);
    } else {
      addColumn(sourceKey, targetKey);
    }
    setDragOverKey(null);
    setDragOverPanel(null);
  };

  const handleAvailableDrop = (event) => {
    event.preventDefault();
    const sourceKey = getDraggedKey(event);
    if (isEnabled(sourceKey)) removeColumn(sourceKey);
    setDragOverKey(null);
    setDragOverPanel(null);
  };

  const reset = () => {
    const allVisible = {};
    for (const column of TEMPLATE_COLUMNS) allVisible[column.viewKey] = true;
    emit({
      columnOrder: TEMPLATE_COLUMNS.map((column) => column.viewKey),
      visibleColumns: allVisible,
      columnLabels: {},
    });
  };

  const dragProps = (viewKey) => ({
    draggable: true,
    onDragStart: (event) => {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData(DRAG_TYPE, viewKey);
      event.dataTransfer.setData("text/plain", viewKey);
      setDragOverKey(null);
    },
    onDragEnd: () => {
      setDragOverKey(null);
      setDragOverPanel(null);
    },
  });

  return (
    <div className="r6-tpl-cols">
      <div className="r6-tpl-cols-head">
        <div className="r6-tpl-cols-title">
          Columns
          <span className="r6-tpl-cols-count">
            {selectedKeys.length} of {TEMPLATE_COLUMNS.length} selected
          </span>
        </div>
        <button type="button" className="r6-link" onClick={reset}>
          Reset all
        </button>
      </div>

      <p className="r6-tpl-cols-help">
        Choose a group, add the fields you need, and drag selected fields to set their order.
      </p>

      <div className="r6-tpl-columns-panels">
        <section
          className={`r6-tpl-column-panel r6-tpl-available-panel ${dragOverPanel === "available" ? "is-panel-drag-over" : ""}`}
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            setDragOverPanel("available");
          }}
          onDrop={handleAvailableDrop}
        >
          <div className="r6-tpl-panel-heading">
            <div className="r6-tpl-panel-title">Available columns</div>
            <span className="r6-tpl-panel-count">{availableKeys.length}</span>
          </div>

          <div className="r6-tpl-group-tabs" role="tablist" aria-label="Column categories">
            {groups.map((group) => {
              const count = TEMPLATE_COLUMNS.filter(
                (column) => column.group === group && !isEnabled(column.viewKey),
              ).length;
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeGroup === group}
                  key={group}
                  className={`r6-tpl-group-tab ${activeGroup === group ? "is-active" : ""}`}
                  onClick={() => setActiveGroup(group)}
                >
                  {group} <span>{count}</span>
                </button>
              );
            })}
          </div>

          <ul className="r6-tpl-panel-list r6-tpl-available-list">
            {availableKeys.map((viewKey) => {
              const def = byKey[viewKey];
              return (
                <li
                  key={viewKey}
                  className={`r6-tpl-available-row ${dragOverKey === `available-${viewKey}` ? "is-drag-over" : ""}`}
                  {...dragProps(viewKey)}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    setDragOverKey(`available-${viewKey}`);
                    setDragOverPanel("available");
                  }}
                  onDrop={handleAvailableDrop}
                >
                  <span className="r6-tpl-available-label">{def.label}</span>
                  <button
                    type="button"
                    className="r6-tpl-add-column"
                    title={`Add ${def.label}`}
                    aria-label={`Add ${def.label}`}
                    onClick={() => addColumn(viewKey)}
                  >
                    <i className="fas fa-plus" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
            {availableKeys.length === 0 && (
              <li className="r6-tpl-panel-empty">All columns in this group are selected.</li>
            )}
          </ul>
          <p className="r6-tpl-panel-hint">Drag a selected field here to remove it.</p>
        </section>

        <section
          className={`r6-tpl-column-panel r6-tpl-selected-panel ${dragOverPanel === "selected" ? "is-panel-drag-over" : ""}`}
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            setDragOverPanel("selected");
          }}
          onDrop={(event) => handleSelectedDrop(event)}
        >
          <div className="r6-tpl-panel-heading">
            <div className="r6-tpl-panel-title">Selected columns</div>
            <span className="r6-tpl-panel-count">{selectedKeys.length}</span>
          </div>
          <p className="r6-tpl-panel-description">These fields appear in the report, from top to bottom.</p>
          <ul className="r6-tpl-panel-list r6-tpl-selected-list">
            {selectedKeys.map((viewKey, index) => {
              const def = byKey[viewKey];
              const label = columnLabels[viewKey] || def.label;
              return (
                <li
                  key={viewKey}
                  className={`r6-tpl-selected-row ${dragOverKey === viewKey ? "is-drag-over" : ""}`}
                  {...dragProps(viewKey)}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    setDragOverKey(viewKey);
                    setDragOverPanel("selected");
                  }}
                  onDrop={(event) => handleSelectedDrop(event, viewKey)}
                >
                  <span className="r6-tpl-selected-grip" aria-hidden="true"><i className="fas fa-grip-vertical" /></span>
                  <span className="r6-tpl-selected-position">{index + 1}.</span>
                  <input
                    className="form-control form-control-sm r6-tpl-col-label"
                    value={label}
                    placeholder={def.label}
                    aria-label={`Report label for ${def.label}`}
                    onChange={(event) => setLabel(viewKey, event.target.value)}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  />
                  <div className="r6-tpl-selected-actions">
                    <button type="button" className="r6-tpl-move" onClick={() => move(viewKey, "up")} disabled={index === 0} title="Move up" aria-label={`Move ${label} up`}>
                      <i className="fas fa-chevron-up" />
                    </button>
                    <button type="button" className="r6-tpl-move" onClick={() => move(viewKey, "down")} disabled={index === selectedKeys.length - 1} title="Move down" aria-label={`Move ${label} down`}>
                      <i className="fas fa-chevron-down" />
                    </button>
                    <button type="button" className="r6-tpl-remove-column" onClick={() => removeColumn(viewKey)} title={`Remove ${label}`} aria-label={`Remove ${label}`}>
                      <i className="fas fa-times" />
                    </button>
                  </div>
                </li>
              );
            })}
            {selectedKeys.length === 0 && (
              <li className="r6-tpl-panel-empty">No columns selected. Add fields from the left panel.</li>
            )}
          </ul>
          <p className="r6-tpl-panel-hint">Drag fields to reorder them, or drag one to the left panel to remove it.</p>
        </section>
      </div>
    </div>
  );
}
